/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import mapboxgl from 'mapbox-gl';
import {
    MapFullscreenControl,
    MapLayerControl,
    MapAttributionControl,
    announce,
    MapResetPosition,
    Map3DToggle,
    MapSearchButtonControl,
    MapFilterControl,
    ShowListButtonControl,
    MapControlContainer,
} from 'sarsaparilla';
import MapLegendControl from './MapLegendControl';
import CampingLegendControl from './CampingLegendControl';
import MapSearchAreaControl from './MapSearchAreaControl';
import MapSearchLayerControl from './MapSearchLayerControl';
import ExpandMapControl from './ExpandMapButton';
import MapAutoSearchControl from './MapAutoSearchControl';

export const addMapControls = (map, params) => {
    let basicControls = {};
    if (!map.current) return basicControls;
    const attributionControl = new MapAttributionControl();

    const navigationControlParams = {
        visualizePitch: true,
        showCompass: !params?.isMobile,
        showZoom: !params?.isMobile,
    };
    const navigationControl = new mapboxgl.NavigationControl(navigationControlParams);
    const mapScaleControl = new mapboxgl.ScaleControl({
        maxWidth: 150,
        unit: 'imperial',
    });

    map.current.addControl(attributionControl, 'bottom-right');
    map.current.addControl(navigationControl, 'top-left');
    map.current.addControl(mapScaleControl);

    basicControls = {
        attribution: attributionControl,
        scale: mapScaleControl,
    };

    if (params.useLayerPanels) {
        const layersPanelControl = new MapControlContainer();
        map.current.addControl(layersPanelControl, 'top-right');
        layersPanelControl.show();
        basicControls.layersPanelControl = layersPanelControl;
    }

    if (!params.ignoreFullscreen) {
        const fullscreenControl = new MapFullscreenControl({
            useModalStyling: params.useModalStyling || false,
        });
        map.current.addControl(fullscreenControl);
        basicControls.fullscreen = fullscreenControl;
    }

    if (!params.ignoreLayers) {
        const mapLayerControl = new MapLayerControl();
        map.current.addControl(mapLayerControl);
        basicControls.layer = mapLayerControl;
    }

    if (params.map3d) {
        const map3DToggle = new Map3DToggle();
        map.current.addControl(map3DToggle);
        basicControls.map3d = map3DToggle;
    }

    if (params.geolocation) {
        const geoParams = {
            positionOptions: { enableHighAccuracy: false },
            fitBoundsOptions: { maxZoom: 9 },
            trackUserLocation: true,
        };
        const geolocation = new mapboxgl.GeolocateControl(geoParams);
        map.current.addControl(geolocation, 'top-left');
    }

    if (navigationControl._zoomInButton) {
        navigationControl._zoomInButton.onclick = () => {
            announce('Zoom in button selected. You have zoomed in the map');
        };
    }

    if (navigationControl._zoomOutButton) {
        navigationControl._zoomOutButton.onclick = () => {
            announce('Zoom out button selected. You have zoomed out the map');
        };
    }

    if (navigationControl._compass) {
        navigationControl._compass.onclick = () => {
            announce(
                'Compass button selected. Map has returned to the default north orientation'
            );
        };
    }

    basicControls.navigation = navigationControl;

    return basicControls;
};

export const addLegendControls = (map, props) => {
    const legendComponent = (
        <CampingLegendControl
            campsites={props.campsites}
            amenities={props.amenities}
            setSelectedLegendFeature={props.setSelectedLegendFeature}
        />
    );
    const legendControl = new MapLegendControl({ listContent: legendComponent });
    map.current.addControl(legendControl, 'bottom-right');
    return legendControl;
};

export const addLayerControls = (map, props) => {
    const layerControl = new MapLayerControl(props.layers);
    map.current.addControl(layerControl);
    return layerControl;
};

export const addMapSpecialControls = (map, params) => {
    const specialControls = {};
    if (params.showList && params.toggleFunc) {
        const showList = new ShowListButtonControl({ onClick: params.toggleFunc });
        map.current.addControl(showList, 'top-left');
        showList.hide();
        specialControls.showList = showList;
    }

    if (params.legend && params.legendContent) {
        const { legendContent, getCurrentZoom, maxZoom, hideStatusLegend } = params;
        const legendControl = new MapLegendControl({
            listContent: legendContent,
            hideStatusLegend,
        });
        map.current.addControl(legendControl, 'bottom-right');
        if (getCurrentZoom && maxZoom) {
            legendControl.updateGlobalContent({
                maxZoom,
                currentZoom: params.getCurrentZoom(),
            });
        }
        specialControls.legend = legendControl;
    }

    if (params.autoSearch && params.searchAreaFunc) {
        const autoSearch = new MapAutoSearchControl(params.searchAreaFunc);
        map.current.addControl(autoSearch, 'top-left');
        specialControls.autoSearch = autoSearch;
    } else if (params.searchArea && params.searchAreaFunc) {
        const searchArea = new MapSearchAreaControl(params.searchAreaFunc);
        map.current.addControl(searchArea, 'top-left');
        searchArea.hide();
        specialControls.searchArea = searchArea;
    }

    if (params.searchLayers && params.layerContent) {
        const searchLayers = new MapSearchLayerControl(...params.layerContent);
        map.current.addControl(searchLayers);
        specialControls.searchLayers = searchLayers;
    }

    if (params.resetPosition) {
        const resetControl = new MapResetPosition({ features: params.data });
        map.current.addControl(resetControl, 'top-left');
        specialControls.resetPosition = resetControl;
    }

    if (params.map3d && !params?.isMobile) {
        const map3DToggle = new Map3DToggle();
        map.current.addControl(map3DToggle, 'top-left');
        specialControls.map3d = map3DToggle;
    }

    if (params.searchNearby) {
        const searchNearby = new MapSearchButtonControl(params.searchNearbyParams);
        map.current.addControl(searchNearby, 'top-left');
        searchNearby.hide();
        specialControls.searchNearby = searchNearby;
    }

    if (params.searchNearbyFilter) {
        const searchNearbyFilter = new MapFilterControl();
        map.current.addControl(searchNearbyFilter, 'top-left');
        searchNearbyFilter.hide();
        specialControls.searchNearbyFilter = searchNearbyFilter;
    }

    if (params.expandButton) {
        const expandMap = new ExpandMapControl(params.expandButton);
        map.current.addControl(expandMap, 'top-left');
        specialControls.expandButton = expandMap;
    }

    return specialControls;
};

export const getControlsPerService = (service) => {
    const controls = {
        pass: { gesture: true, useModalStyling: true },
        destination: { gesture: true },
        treepermit: { gesture: true },
        ticket: { useModalStyling: true },
        campingpoi: { gesture: true },
        navigation: { geolocation: true, ignoreFullscreen: true, gesture: true },
        gateway: {
            geolocation: true,
            gesture: true,
            ignoreFullscreen: true,
            ignoreLayers: true,
            useLayerPanels: true,
        },
        search: { ignoreLayers: true, useLayerPanels: true },
        camping: { ignoreLayers: true, useModalStyling: true },
        campingitinerary: {
            ignoreLayers: true,
            useModalStyling: true,
        },
        permit: { gesture: true },
        tripplanner: { ignoreLayers: true },
        campingsinglepoint: { useModalStyling: true },
        campinginternal: { useModalStyling: true },
    };

    return controls[service] || {};
};

export const getSpecialControlsPerService = (service) => {
    const controls = {
        navigation: {
            showList: true,
            legend: true,
            hideStatusLegend: true,
            searchNearbyFilter: true,
        },
        gateway: {
            legend: true,
            hideStatusLegend: true,
        },
        search: {
            legend: true,
            hideStatusLegend: true,
            autoSearch: true,
        },
        camping: {
            resetPosition: true,
            map3d: true,
        },
        tripplanner: {
            legend: true,
            searchLayers: true,
            hideStatusLegend: true,
        },
        internalcampingfieldsales: {},
        campinginternal: {
            hideStatusLegend: true,
        },
    };
    return controls[service] || {};
};

// cSpell:ignore geolocate, campinginternal, campingitinerary, campingpoi, campingsinglepoint, Controlv, internalcampingfieldsales, treepermit, tripplanner
